import React from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import Layout from "../../components/Layout.tsx";
import ModalContainer from "../../components/ModalContainer.tsx";
import { useFormik } from "formik";

interface NewFormPreguntasInicialesProps {
  nombre: string;
  apellidoP: string;
  apellidoM: string;
  nacimiento: string;
  curp: string;
  schoolings: any[];
  positions: any[];
  shifts: any[];
  interviewDate: Date | null;
  horas: string[] | undefined;
  horasAgendadas: Record<string, string>;
  uniqueBranches: any[];
  fechasAvaibles: string[];
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void> | void;
  handleChangeDate: (date: Date) => Promise<void> | void;
  getAvaibles: (id: number | string) => Promise<void> | void;
}

const NewFormPreguntasIniciales: React.FC<NewFormPreguntasInicialesProps> = ({
  nombre,
  apellidoP,
  apellidoM,
  nacimiento,
  curp,
  schoolings,
  positions,
  shifts,
  interviewDate,
  horas,
  horasAgendadas,
  uniqueBranches,
  fechasAvaibles = [],
  handleSubmit,
  handleChangeDate,
  getAvaibles,
}) => {
  const todayIso = new Date().toISOString().slice(0, 10);

  const [openPermanenciaModal, setOpenPermanenciaModal] = React.useState(false);
  const [permanenciaRange, setPermanenciaRange] = React.useState<string>("");
  const [permanenciaValue, setPermanenciaValue] = React.useState<string>("");
  const [hasSelectedPosition, setHasSelectedPosition] =
    React.useState<boolean>(false);

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    const len = digits.length;

    if (len === 0) return "";
    if (len <= 3) return `(${digits}`;
    if (len <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (len <= 8)
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`;

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(
      6,
      8
    )} ${digits.slice(8)}`;
  };

  const filterDates = (date: Date) => {
    const formattedDate = moment(date).format("YYYY-MM-DD");
    return fechasAvaibles.includes(formattedDate);
  };

  const renderDayContents = (day: number, date: Date) => {
    const isSelected =
      interviewDate && moment(date).isSame(moment(interviewDate), "day");
    const today = moment();
    const isToday = moment(date).isSame(today, "day");
    const isPast = moment(date).isBefore(today, "day");
    const formattedDate = moment(date).format("YYYY-MM-DD");
    const hasAvailability = fechasAvaibles.includes(formattedDate);
    const isDisabled = !hasAvailability;

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 6,
          backgroundColor: isSelected
            ? "rgba(37, 99, 235, 0.2)"
            : isToday && hasAvailability
            ? "rgba(34, 197, 94, 0.18)"
            : "transparent",
          color: isDisabled || isPast ? "#9ca3af" : "#111827",
          boxSizing: "border-box",
          opacity: isDisabled ? 0.5 : 1,
        }}
      >
        {day}
      </div>
    );
  };
  const sectionCardStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: 16,
    padding: "20px 24px",
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.18)",
    marginBottom: 20,
  };

  const sectionHeaderStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    marginBottom: 16,
  };

  const stepCircleStyle: React.CSSProperties = {
    width: 26,
    height: 26,
    borderRadius: "999px",
    backgroundColor: "#FA0A0A",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: "#9ca3af",
    marginBottom: 4,
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    padding: "10px 12px",
    backgroundColor: "#F3F4F6",
    fontSize: 13,
    fontWeight: 600,
    outline: "none",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  };

  const inputGroupStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    marginBottom: 12,
  };

  const errorTextStyle: React.CSSProperties = {
    color: "#dc2626",
    fontSize: 11,
    marginTop: 2,
  };

  const updateFieldAndValidate = (name: string, value: any) => {
    formik.setFieldValue(name, value, false);

    const currentValues: any = { ...(formik.values as any), [name]: value };
    const allErrors = validate(currentValues);

    formik.setErrors({
      ...(formik.errors as any),
      [name]: allErrors[name],
    });
  };

  const hasError = (field: string) =>
    (formik.touched as any)[field] &&
    typeof (formik.errors as any)[field] === "string";

  const getFieldStyle = (field: string): React.CSSProperties =>
    hasError(field)
      ? {
          ...inputStyle,
          borderColor: "#dc2626",
          backgroundColor: "#fef2f2",
        }
      : inputStyle;

  const validate = (values: any) => {
    const errors: any = {};

    if (!values.nombre) errors.nombre = "Campo requerido";
    if (!values.apellido_p) errors.apellido_p = "Campo requerido";
    if (!values.apellido_m) errors.apellido_m = "Campo requerido";
    if (!values.nacimiento) errors.nacimiento = "Campo requerido";
    if (!values.escolaridad) errors.escolaridad = "Campo requerido";

    if (!values.telefono) {
      errors.telefono = "Campo requerido";
    } else {
      const digits = String(values.telefono).replace(/\D/g, "");
      if (digits.length !== 10) {
        errors.telefono = "Debe contener 10 dígitos";
      }
    }

    if (!values.direccion) errors.direccion = "Campo requerido";
    if (!values.puesto) errors.puesto = "Campo requerido";
    if (!values.expectativaSalarial)
      errors.expectativaSalarial = "Campo requerido";

    if (!values.permanencia) errors.permanencia = "Selecciona un rango";
    if (!values.experiencia) errors.experiencia = "Campo requerido";
    if (!values.horario) errors.horario = "Campo requerido";

    if (!values.preferenciaLugarTipo)
      errors.preferenciaLugarTipo = "Campo requerido";

    if (!values.preferenciaSucursal)
      errors.preferenciaSucursal = "Campo requerido";

    if (hasSelectedPosition) {
      if (!values.entrevista) errors.entrevista = "Selecciona la fecha";
      if (!values.horarioE) errors.horarioE = "Selecciona la hora";
    }

    return errors;
  };

  const formik = useFormik<any>({
    initialValues: {},
    validate,
    onSubmit: () => {},
    validateOnBlur: false,
    validateOnChange: false,
  });

  const scrollToFirstError = (errors: any) => {
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) return;

    // Orden de prioridad de los campos según aparecen en el formulario
    const fieldOrder = [
      "nombre",
      "apellido_p",
      "apellido_m",
      "nacimiento",
      "escolaridad",
      "telefono",
      "direccion",
      "puesto",
      "expectativaSalarial",
      "permanencia",
      "experiencia",
      "horario",
      "preferenciaLugarTipo",
      "preferenciaSucursal",
      "entrevista",
      "horarioE",
    ];

    // Mapeo de campos especiales que tienen IDs diferentes
    const fieldIdMap: Record<string, string> = {
      permanencia: "permanenciaDisplay",
    };

    // Encontrar el primer campo con error según el orden del formulario
    const firstErrorField = fieldOrder.find((field) => errors[field]);

    if (firstErrorField) {
      // Usar el ID mapeado si existe, de lo contrario usar el nombre del campo
      const elementId = fieldIdMap[firstErrorField] || firstErrorField;
      const element = document.getElementById(elementId);

      if (element) {
        // Calcular la posición con un offset para que no quede pegado al borde
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 120;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });

        // Hacer focus en el campo después de un pequeño delay para asegurar el scroll
        setTimeout(() => {
          element.focus();
          // Para campos especiales como DatePicker, intentar hacer focus en el input interno
          if (firstErrorField === "entrevista") {
            const datePickerInput = element.querySelector("input");
            if (datePickerInput) {
              datePickerInput.focus();
            }
          }
        }, 300);
      }
    }
  };

  const handleFormSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const values: any = {};
    formData.forEach((value, key) => {
      values[key] = value;
    });

    const errors = validate(values);
    formik.setErrors(errors);

    const touched: any = {};
    Object.keys(values).forEach((key) => {
      touched[key] = true;
    });
    formik.setTouched(touched, false);

    if (Object.keys(errors).length > 0) {
      // Hacer scroll al primer error
      setTimeout(() => {
        scrollToFirstError(errors);
      }, 100);
      return;
    }

    await handleSubmit(e);
  };

  const gridTwoCols: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginTop: 8,
  };

  const gridThreeCols: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 16,
    marginTop: 8,
  };

  const handleInputFocus = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    e.currentTarget.style.borderColor = "#111827";
    e.currentTarget.style.boxShadow = "0 0 0 1px #11182720";
  };

  const handleInputBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    e.currentTarget.style.borderColor = "#e5e7eb";
    e.currentTarget.style.boxShadow = "none";
  };

  return (
    <Layout>
      <style>{`
        .react-datepicker__navigation--previous {
          display: none !important;
        }
        .react-datepicker__month-dropdown-container,
        .react-datepicker__year-dropdown-container {
          pointer-events: none;
        }
        .react-datepicker__month-dropdown,
        .react-datepicker__year-dropdown {
          opacity: 0.5;
        }
        .react-datepicker-popper {
          z-index: 9999 !important;
        }
      `}</style>
      <div
        style={{
          minHeight: "100vh",
          background:
            "radial-gradient(circle at top left, #FBFBFB 0, #E5E7EB 40%, #D1D5DB 100%)",
          padding: "24px 0 40px",
        }}
      >
        <div
          style={{
            width: "100%",
            padding: "0 24px",
            boxSizing: "border-box",
            marginBottom: 24,
          }}
        >
          <div
            style={{
              width: "100%",
              borderRadius: 16,
              backgroundColor: "#111827",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 16,
              boxShadow: "0 6px 14px rgba(15, 23, 42, 0.25)",
              padding: "10px 24px",
            }}
          >
            <img
              src="../assets/img/logo/logo_chili_rojo.png"
              alt=""
              style={{
                maxWidth: 170,
                maxHeight: 48,
                objectFit: "contain",
                display: "block",
              }}
            />
            <span
              style={{
                color: "#F9FAFB",
                fontSize: 16,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              Aplicación para vacante
            </span>
          </div>
        </div>

        <div
          style={{
            width: "100%",
            padding: "0 24px",
            boxSizing: "border-box",
            margin: "0 auto",
          }}
        >
          <form onSubmit={handleFormSubmit} style={{ marginBottom: 40 }}>
            <div style={sectionCardStyle}>
              <div style={sectionHeaderStyle}>
                <div style={stepCircleStyle}>1</div>
                <div>
                  <div
                    style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}
                  >
                    Datos generales del aspirante
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#6b7280",
                      marginTop: 2,
                    }}
                  >
                    Información básica necesaria para el contacto y tu registro.
                  </div>
                </div>
              </div>

              <div style={gridThreeCols}>
                <div style={inputGroupStyle}>
                  <label htmlFor="nombre" style={labelStyle}>
                    Nombre(s)
                  </label>
                  <input
                    id="nombre"
                    name="nombre"
                    defaultValue={nombre}
                    type="text"
                    style={getFieldStyle("nombre")}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                  {formik.touched.nombre &&
                    typeof formik.errors.nombre === "string" && (
                      <span style={errorTextStyle}>{formik.errors.nombre}</span>
                    )}
                </div>
                <div style={inputGroupStyle}>
                  <label htmlFor="apellido_p" style={labelStyle}>
                    Apellido paterno
                  </label>
                  <input
                    id="apellido_p"
                    name="apellido_p"
                    defaultValue={apellidoP}
                    type="text"
                    style={getFieldStyle("apellido_p")}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                  {formik.touched.apellido_p &&
                    typeof formik.errors.apellido_p === "string" && (
                      <span style={errorTextStyle}>
                        {formik.errors.apellido_p}
                      </span>
                    )}
                </div>
                <div style={inputGroupStyle}>
                  <label htmlFor="apellido_m" style={labelStyle}>
                    Apellido materno
                  </label>
                  <input
                    id="apellido_m"
                    name="apellido_m"
                    defaultValue={apellidoM}
                    type="text"
                    style={getFieldStyle("apellido_m")}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                  {formik.touched.apellido_m &&
                    typeof formik.errors.apellido_m === "string" && (
                      <span style={errorTextStyle}>
                        {formik.errors.apellido_m}
                      </span>
                    )}
                </div>
              </div>

              <div style={gridThreeCols}>
                <div style={inputGroupStyle}>
                  <label htmlFor="nacimiento" style={labelStyle}>
                    Fecha de nacimiento
                  </label>
                  <input
                    id="nacimiento"
                    name="nacimiento"
                    defaultValue={nacimiento}
                    type="date"
                    max={todayIso}
                    style={getFieldStyle("nacimiento")}
                    onFocus={handleInputFocus}
                    onBlur={(e) => {
                      handleInputBlur(e);
                      formik.handleBlur(e);
                      updateFieldAndValidate(e.target.name, e.target.value);
                    }}
                  />
                  {formik.touched.nacimiento &&
                    typeof formik.errors.nacimiento === "string" && (
                      <span style={errorTextStyle}>
                        {formik.errors.nacimiento}
                      </span>
                    )}
                </div>
                <div style={inputGroupStyle}>
                  <label htmlFor="escolaridad" style={labelStyle}>
                    Escolaridad
                  </label>
                  <select
                    id="escolaridad"
                    name="escolaridad"
                    style={getFieldStyle("escolaridad")}
                    onFocus={handleInputFocus}
                    onBlur={(e) => {
                      handleInputBlur(e);
                      formik.handleBlur(e);
                      updateFieldAndValidate(e.target.name, e.target.value);
                    }}
                  >
                    <option value="">Seleccione una opción</option>
                    {schoolings.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  {formik.touched.escolaridad &&
                    typeof formik.errors.escolaridad === "string" && (
                      <span style={errorTextStyle}>
                        {formik.errors.escolaridad}
                      </span>
                    )}
                </div>
                <div style={inputGroupStyle}>
                  <label htmlFor="telefono" style={labelStyle}>
                    Teléfono
                  </label>
                  <input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    inputMode="numeric"
                    placeholder="(999) 123 45 67"
                    style={getFieldStyle("telefono")}
                    onChange={(e) => {
                      e.target.value = formatPhone(e.target.value);
                      updateFieldAndValidate(e.target.name, e.target.value);
                    }}
                    onFocus={handleInputFocus}
                    onBlur={(e) => {
                      handleInputBlur(e);
                      formik.handleBlur(e);
                    }}
                  />
                  {formik.touched.telefono &&
                    typeof formik.errors.telefono === "string" && (
                      <span style={errorTextStyle}>
                        {formik.errors.telefono}
                      </span>
                    )}
                </div>
              </div>

              <div style={gridTwoCols}>
                <div style={inputGroupStyle}>
                  <label htmlFor="direccion" style={labelStyle}>
                    Domicilio completo
                  </label>
                  <input
                    id="direccion"
                    name="direccion"
                    type="text"
                    placeholder="Calle, número, colonia, código postal"
                    style={getFieldStyle("direccion")}
                    onFocus={handleInputFocus}
                    onBlur={(e) => {
                      handleInputBlur(e);
                      formik.handleBlur(e);
                      updateFieldAndValidate(e.target.name, e.target.value);
                    }}
                  />
                  {formik.touched.direccion &&
                    typeof formik.errors.direccion === "string" && (
                      <span style={errorTextStyle}>
                        {formik.errors.direccion}
                      </span>
                    )}
                </div>
                <div style={inputGroupStyle}>
                  <label htmlFor="curp" style={labelStyle}>
                    CURP (solo lectura)
                  </label>
                  <input
                    id="curp"
                    name="curp"
                    type="text"
                    defaultValue={curp}
                    disabled
                    style={{
                      ...inputStyle,
                      backgroundColor: "#f9fafb",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={sectionCardStyle}>
              <div style={sectionHeaderStyle}>
                <div style={stepCircleStyle}>2</div>
                <div>
                  <div
                    style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}
                  >
                    Datos del empleo y preferencias
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#6b7280",
                      marginTop: 2,
                    }}
                  >
                    Cuéntanos qué buscas y cuándo podrías asistir a entrevista.
                  </div>
                </div>
              </div>

              <div style={gridThreeCols}>
                <div style={inputGroupStyle}>
                  <label htmlFor="puesto" style={labelStyle}>
                    Puesto al que aplicas
                  </label>
                  <select
                    id="puesto"
                    name="puesto"
                    style={getFieldStyle("puesto")}
                    onChange={(e) => {
                      const id = Number(e.target.value);
                      setHasSelectedPosition(!!id);
                      getAvaibles(id);
                      updateFieldAndValidate(e.target.name, e.target.value);
                    }}
                    onFocus={handleInputFocus}
                    onBlur={(e) => {
                      handleInputBlur(e);
                      formik.handleBlur(e);
                    }}
                  >
                    <option value="">Seleccione una opción</option>
                    {positions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.position.name}
                      </option>
                    ))}
                  </select>
                  {formik.touched.puesto &&
                    typeof formik.errors.puesto === "string" && (
                      <span style={errorTextStyle}>{formik.errors.puesto}</span>
                    )}
                </div>
                <div style={inputGroupStyle}>
                  <label htmlFor="expectativaSalarial" style={labelStyle}>
                    Expectativa salarial al mes
                  </label>
                  <input
                    id="expectativaSalarial"
                    name="expectativaSalarial"
                    type="number"
                    min={0}
                    style={getFieldStyle("expectativaSalarial")}
                    onFocus={handleInputFocus}
                    onBlur={(e) => {
                      handleInputBlur(e);
                      formik.handleBlur(e);
                      updateFieldAndValidate(e.target.name, e.target.value);
                    }}
                  />
                  {formik.touched.expectativaSalarial &&
                    typeof formik.errors.expectativaSalarial === "string" && (
                      <span style={errorTextStyle}>
                        {formik.errors.expectativaSalarial}
                      </span>
                    )}
                </div>
                <div style={inputGroupStyle}>
                  <label htmlFor="permanencia" style={labelStyle}>
                    Expectativa de permanencia (meses)
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      id="permanenciaDisplay"
                      readOnly
                      placeholder="Selecciona un rango"
                      value={permanenciaRange}
                      style={{
                        ...(getFieldStyle(
                          "permanencia"
                        ) as React.CSSProperties),
                        cursor: "pointer",
                      }}
                      onClick={() => setOpenPermanenciaModal(true)}
                      onFocus={handleInputFocus}
                      onBlur={(e) => {
                        handleInputBlur(e);
                        formik.handleBlur(e);
                      }}
                    />
                  </div>

                  <input
                    type="hidden"
                    id="permanencia"
                    name="permanencia"
                    value={permanenciaValue}
                  />
                  <input
                    type="hidden"
                    id="permanenciaRango"
                    name="permanenciaRango"
                    value={permanenciaRange}
                  />
                  {formik.touched.permanencia &&
                    typeof formik.errors.permanencia === "string" && (
                      <span style={errorTextStyle}>
                        {formik.errors.permanencia}
                      </span>
                    )}
                </div>
              </div>

              <div style={{ ...gridTwoCols, gridTemplateColumns: "1fr" }}>
                <div style={inputGroupStyle}>
                  <label htmlFor="experiencia" style={labelStyle}>
                    Describe tu experiencia en el área
                  </label>
                  <textarea
                    id="experiencia"
                    name="experiencia"
                    rows={4}
                    style={{
                      ...(getFieldStyle("experiencia") as React.CSSProperties),
                      resize: "vertical",
                      minHeight: 90,
                    }}
                    onFocus={handleInputFocus}
                    onBlur={(e) => {
                      handleInputBlur(e);
                      formik.handleBlur(e);
                      updateFieldAndValidate(e.target.name, e.target.value);
                    }}
                  ></textarea>
                  {formik.touched.experiencia &&
                    typeof formik.errors.experiencia === "string" && (
                      <span style={errorTextStyle}>
                        {formik.errors.experiencia}
                      </span>
                    )}
                </div>
              </div>

              <div style={gridThreeCols}>
                <div style={inputGroupStyle}>
                  <label htmlFor="horario" style={labelStyle}>
                    Preferencia de turno
                  </label>
                  <select
                    id="horario"
                    name="horario"
                    style={getFieldStyle("horario")}
                    onFocus={handleInputFocus}
                    onBlur={(e) => {
                      handleInputBlur(e);
                      formik.handleBlur(e);
                      updateFieldAndValidate(e.target.name, e.target.value);
                    }}
                  >
                    <option value="">Seleccione una opción</option>
                    {shifts.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  {formik.touched.horario &&
                    typeof formik.errors.horario === "string" && (
                      <span style={errorTextStyle}>
                        {formik.errors.horario}
                      </span>
                    )}
                </div>
                <div style={inputGroupStyle}>
                  <label htmlFor="preferenciaLugarTipo" style={labelStyle}>
                    Preferencia de tipo de lugar
                  </label>
                  <select
                    id="preferenciaLugarTipo"
                    name="preferenciaLugarTipo"
                    style={getFieldStyle("preferenciaLugarTipo")}
                    onFocus={handleInputFocus}
                    onBlur={(e) => {
                      handleInputBlur(e);
                      formik.handleBlur(e);
                      updateFieldAndValidate(e.target.name, e.target.value);
                    }}
                  >
                    <option value="">Seleccione una opción</option>
                    <option value="Sucursal">Sucursal</option>
                    <option value="Planta de producción">
                      Planta de producción
                    </option>
                  </select>
                  {formik.touched.preferenciaLugarTipo &&
                    typeof formik.errors.preferenciaLugarTipo === "string" && (
                      <span style={errorTextStyle}>
                        {formik.errors.preferenciaLugarTipo}
                      </span>
                    )}
                </div>
                <div style={inputGroupStyle}>
                  <label htmlFor="preferenciaSucursal" style={labelStyle}>
                    Sucursal preferente
                  </label>
                  <select
                    id="preferenciaSucursal"
                    name="preferenciaSucursal"
                    style={getFieldStyle("preferenciaSucursal")}
                    onFocus={handleInputFocus}
                    onBlur={(e) => {
                      handleInputBlur(e);
                      formik.handleBlur(e);
                      updateFieldAndValidate(e.target.name, e.target.value);
                    }}
                  >
                    <option value="">
                      {uniqueBranches && uniqueBranches.length > 0
                        ? "Seleccione una opción"
                        : "No hay sucursales disponibles"}
                    </option>
                    {uniqueBranches &&
                      uniqueBranches.length > 0 &&
                      uniqueBranches.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                  </select>
                  {formik.touched.preferenciaSucursal &&
                    typeof formik.errors.preferenciaSucursal === "string" && (
                      <span style={errorTextStyle}>
                        {formik.errors.preferenciaSucursal}
                      </span>
                    )}
                </div>
              </div>

              {hasSelectedPosition && (
                <div style={{ ...gridTwoCols, marginTop: 20 }}>
                  <div style={inputGroupStyle}>
                    <label htmlFor="entrevista" style={labelStyle}>
                      Fecha de entrevista
                    </label>
                    <div
                      style={{
                        ...(getFieldStyle("entrevista") as React.CSSProperties),
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <DatePicker
                        locale="es"
                        dateFormat="dd/MM/yyyy"
                        selected={interviewDate}
                        onChange={handleChangeDate}
                        name="entrevista"
                        id="entrevista"
                        filterDate={filterDates}
                        renderDayContents={renderDayContents}
                        minDate={moment().startOf("month").toDate()}
                        placeholderText="Selecciona la fecha"
                        wrapperClassName="w-100"
                        popperPlacement="top-start"
                        customInput={
                          <input
                            style={{
                              border: "none",
                              outline: "none",
                              padding: "8px 10px",
                              fontSize: 13,
                              fontWeight: 600,
                              width: "100%",
                              backgroundColor: "transparent",
                            }}
                          />
                        }
                      />
                    </div>
                    {formik.touched.entrevista &&
                      typeof formik.errors.entrevista === "string" && (
                        <span style={errorTextStyle}>
                          {formik.errors.entrevista}
                        </span>
                      )}
                  </div>
                  <div style={inputGroupStyle}>
                    <label htmlFor="horarioE" style={labelStyle}>
                      Hora de la entrevista
                    </label>
                    <select
                      id="horarioE"
                      name="horarioE"
                      disabled={!interviewDate}
                      style={{
                        ...getFieldStyle("horarioE"),
                        ...(!interviewDate
                          ? {
                              backgroundColor: "#f9fafb",
                              cursor: "not-allowed",
                              opacity: 0.6,
                            }
                          : {}),
                      }}
                      onFocus={handleInputFocus}
                      onBlur={(e) => {
                        handleInputBlur(e);
                        formik.handleBlur(e);
                        updateFieldAndValidate(e.target.name, e.target.value);
                      }}
                    >
                      <option value="">
                        {interviewDate
                          ? "Selecciona una hora"
                          : "Primero selecciona una fecha"}
                      </option>
                      {horas?.map((item, index) => {
                        const horarender = horasAgendadas[item] ? null : (
                          <option key={index} value={item}>
                            {item}
                          </option>
                        );
                        return horarender;
                      })}
                    </select>
                    {formik.touched.horarioE &&
                      typeof formik.errors.horarioE === "string" && (
                        <span style={errorTextStyle}>
                          {formik.errors.horarioE}
                        </span>
                      )}
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                ...sectionCardStyle,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <button
                type="submit"
                style={{
                  borderRadius: 999,
                  border: "none",
                  backgroundColor: "#FA0A0A",
                  color: "#ffffff",
                  padding: "10px 32px",
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  cursor: "pointer",
                  boxShadow: "0 12px 30px rgba(220, 44, 39, 0.55)",
                }}
              >
                Enviar
              </button>
            </div>
          </form>
        </div>

        <ModalContainer
          open={openPermanenciaModal}
          closeFn={() => setOpenPermanenciaModal(false)}
          title="Selecciona expectativa de permanencia"
        >
          <div className="row">
            {[
              { label: "1 - 2 meses", value: "2" },
              { label: "2 - 3 meses", value: "3" },
              { label: "3 - 4 meses", value: "4" },
              { label: "4 - 5 meses", value: "5" },
              { label: "5 - 6 meses", value: "6" },
              { label: "6 - 7 meses", value: "7" },
              { label: "7 - 8 meses", value: "8" },
              { label: "8 - 9 meses", value: "9" },
              { label: "9 - 10 meses", value: "10" },
              { label: "10 - 11 meses", value: "11" },
              { label: "11 - 12 meses", value: "12" },
              { label: "12 meses o más", value: "12" },
            ].map((item) => (
              <div className="col-6 mb-2" key={item.label}>
                <button
                  type="button"
                  className="w-100 text-start"
                  onClick={() => {
                    setPermanenciaRange(item.label);
                    setPermanenciaValue(item.value);
                    setOpenPermanenciaModal(false);
                    updateFieldAndValidate("permanencia", item.value);
                    updateFieldAndValidate("permanenciaRango", item.label);
                    formik.setTouched(
                      { ...(formik.touched as any), permanencia: true },
                      false
                    );
                  }}
                  style={{
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    padding: "10px 12px",
                    fontSize: 13,
                    fontWeight: 600,
                    backgroundColor:
                      permanenciaRange === item.label ? "#111827" : "#f3f4f6",
                    color:
                      permanenciaRange === item.label ? "#ffffff" : "#4b5563",
                    cursor: "pointer",
                    transition:
                      "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
                  }}
                >
                  {item.label}
                </button>
              </div>
            ))}
          </div>
        </ModalContainer>
      </div>
    </Layout>
  );
};

export default NewFormPreguntasIniciales;
